import {useCallback} from 'react';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useChekinSDK} from 'context/ChekinSDK';
import {useReservation} from 'context/reservation';
import {QUERY_CACHE_KEYS} from 'utils/constants';
import {GuestGroup, GuestGroupPayload} from '@guestapp/sdk';
import {toastResponseError} from 'utils/common';

export type ErrorRequest = {
  code: string;
  errors: {
    message: string;
  }[];
  message: string;
  status: number;
  status_code: number;
};

function useGuestGroupMutation() {
  const {patchGuestGroup} = useChekinSDK();
  const {data: reservation} = useReservation();
  const guestGroupId = reservation?.guest_group_id;
  const queryClient = useQueryClient();

  const {mutate, isLoading, error} = useMutation<
    GuestGroup,
    ErrorRequest,
    {guestGroupId: string; payload: GuestGroupPayload}
  >(({payload, guestGroupId}) => patchGuestGroup(guestGroupId, payload), {
    onSuccess: () => {
      void queryClient.invalidateQueries([QUERY_CACHE_KEYS.seasonGuests]);
      void queryClient.refetchQueries([QUERY_CACHE_KEYS.summary]);
      void queryClient.refetchQueries([QUERY_CACHE_KEYS.guestGroup]);
    },
    onError: error => {
      if (error.status_code === 400) {
        toastResponseError(error);
        console.error(error);
        return;
      }
    },
  });

  const updateGuestGroup = useCallback(
    (payload: GuestGroupPayload, options: Parameters<typeof mutate>[1]) => {
      if (!guestGroupId) return;
      mutate({guestGroupId, payload}, options);
    },
    [mutate, guestGroupId],
  );

  return {
    error,
    isLoading: isLoading,
    updateGuestGroup,
  };
}

export {useGuestGroupMutation};
